import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { monadClient } from '../../api/client'
import { viewTheme } from '../../views/theme'
import { registerHintWidget, registerWidget, type CellContentProps, type CellEditorProps } from '../registry'
import { controlChrome, editorBehavior } from '../editorChrome'
import { Button } from '../Button'

export function ColorContent({ value }: CellContentProps) {
  const color = typeof value === 'string' && value ? value.toLowerCase() : null
  if (!color) return <span className="text-muted-foreground">—</span>
  return (
    <span className="inline-flex items-center gap-1.5">
      <span data-testid="color-swatch" className="h-3 w-3 rounded-sm border border-border" style={{ backgroundColor: color }} />
      <span className="font-mono text-xs">{color}</span>
    </span>
  )
}

export function ColorEditor({ value, onCommit, onCancel, context, slug }: CellEditorProps) {
  const [draft, setDraft] = useState(typeof value === 'string' ? value : '')
  const [invalid, setInvalid] = useState(false)
  const behavior = editorBehavior(context)
  const { data: subtypes } = useQuery({
    queryKey: ['subtypes', slug],
    queryFn: () => monadClient.listSubtypes(slug!),
    enabled: Boolean(slug),
    staleTime: 300_000,
  })
  const patternSource = subtypes?.find((subtype) => subtype.name === 'color')
    ?.value_schema?.pattern
  const pattern = useMemo(() => {
    if (typeof patternSource !== 'string') return null
    try { return new RegExp(patternSource) } catch { return null }
  }, [patternSource])
  const isValid = (next: string) => Boolean(pattern?.test(next.trim().toLowerCase()))

  const commit = (next: string) => {
    const normalized = next.trim().toLowerCase()
    if (!isValid(normalized)) {
      setInvalid(true)
      return
    }
    setInvalid(false)
    onCommit(normalized)
  }

  return (
    <div data-testid="color-picker" className={`space-y-2 rounded-sm bg-background p-2 ${controlChrome(context)}`}>
      <div className="grid grid-cols-6 gap-1">
        {viewTheme.identity.palette.map((color) => (
          <Button key={color} type="button" variant="bare" size="none" aria-label={`Choose ${color}`}
            className="h-6 w-6 rounded-sm border border-border" style={{ backgroundColor: color }}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => { setInvalid(false); setDraft(color); onCommit(color) }} />
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input data-testid="cell-editor" autoFocus={behavior.autoFocus} value={draft}
          data-validation-ready={Boolean(pattern)}
          aria-invalid={invalid || undefined}
          className={`min-w-0 flex-1 rounded-sm bg-background px-2 py-1 text-sm outline-none ${controlChrome(context)}`}
          onChange={(event) => { setDraft(event.target.value); setInvalid(false) }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') { event.preventDefault(); commit(draft) }
            if (event.key === 'Escape') {
              event.preventDefault()
              if (behavior.escapeIsLocal) event.stopPropagation()
              onCancel()
            }
          }}
          onBlur={() => {
            if (!pattern) return
            if (isValid(draft)) commit(draft); else onCancel()
          }} />
      </div>
      {invalid && <p role="alert" className="text-xs text-destructive">Use a six-digit colour in #rrggbb form.</p>}
    </div>
  )
}

const colorWidget = { read: ColorContent, write: ColorEditor, defaultWidth: 150 }
registerWidget('color', colorWidget)
registerHintWidget('color', colorWidget)
